import { useDidMount } from 'lib/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { useImmer } from 'use-immer';
import { icon_picture } from 'components/base/images';
import { AirlineSeatIndividualSuiteSharp } from '@material-ui/icons';
import axios from 'axios';

/**
 * Dropzone Component
 * @param {number} width - styles가 없을 경우 width 적용, 기본 100%
 * @param {number} height - styles가 없을 경우 height 적용, 기본 100%
 * @param {object} styles - style property 객체
 * @param {function} onSetVisible - dropzoneView visible설정을 위해 DropzoneWrapper에서 받은 function
 * @param {function} apiRequest - 파일 업로드 API 요청 함수
 * @param {boolean} isView - dropzone view 표시 여부
 */
function Dropzone({ width, height, styles, onSetVisible, apiRequest, isView }) {
  const [dropFiles, setDropFiles] = useState([]);
  const [isRequest, setIsRequest] = useState(false);

  // 파일 드롭 핸들러
  const onDrop = useCallback(async acceptedFiles => {
    console.log('[onDrop] Accepted files:', acceptedFiles);
    
    // 파일 path 속성 확인 (Electron 환경)
    acceptedFiles.forEach((file, index) => {
      console.log(`[File ${index}]:`, {
        name: file.name,
        path: file.path || 'N/A',
        size: file.size,
        type: file.type
      });
    });
    
    setDropFiles(prevFiles => prevFiles.concat(acceptedFiles));
    setIsRequest(true);
  }, []);

  // Electron webview에서 파일 경로를 가져오기 위한 커스텀 파일 getter
  // GitHub issue #1411 해결 방법
  const getFilesFromEvent = useCallback(async (event) => {
    const files = [];
    const fileList = event.dataTransfer 
      ? event.dataTransfer.files 
      : event.target.files;

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList.item(i);
      
      // Electron 환경에서 webUtils 사용 시도
      try {
        if (window.require) {
          const { webUtils } = window.require('electron');
          if (webUtils && webUtils.getPathForFile) {
            Object.defineProperty(file, 'path', {
              value: webUtils.getPathForFile(file),
              writable: false,
              configurable: true
            });
            console.log('[Electron] File path resolved:', file.path);
          }
        }
      } catch (error) {
        console.log('[Electron] webUtils not available');
      }
      
      files.push(file);
    }
    
    return files;
  }, []);

  // react-dropzone 설정
  const { getRootProps, getInputProps } = useDropzone({ 
    onDrop,
    getFilesFromEvent
  });

  const rootProps = {
    ...getRootProps(),
    maxsize: 100,
    onDragLeave: () => onSetVisible(false),
  };

  const inputProps = {
    ...getInputProps(),
  };

  // 파일 드롭 완료 후 API 요청 및 dropzone 숨기기
  useEffect(() => {
    if (isRequest) {
      apiRequest({ dropFiles });
      onSetVisible(false);
      setIsRequest(false);
    }
  }, [isRequest, dropFiles, apiRequest, onSetVisible]);

  // 파일 변경 감지
  useEffect(() => {
    if (dropFiles.length > 0) {
      console.log('[Dropzone] Files updated:', dropFiles.length, 'files');
    }
  }, [dropFiles]);

  return (
    <Styled.Dropzone
      data-component-name="Dropzone"
      style={styles}
      width={width}
      height={height}
      className={!styles && isView ? 'default' : 'noFrame'}
      onDragLeave={() => onSetVisible(false)}
      onDrop={event => {
        if (!event.dataTransfer.types.some(item => item === 'Files')) {
          onSetVisible(false);
        }
      }}
    >
      {isView && (
        <div className="dropzoneView">
          <div className="dropzoneView__picture_box">
            <div className="dropzoneView__picture_img">
              <img src={icon_picture} alt="picture" />
            </div>
            <div className="dropzoneView__picture_text">Drag&amp;Drop File here</div>
          </div>
        </div>
      )}
      <div {...rootProps} className="dropzone__container">
        <input {...inputProps} />
      </div>
    </Styled.Dropzone>
  );
}

const Styled = {
  Dropzone: styled.div`
    &.default,
    .dropzone__container {
      position: absolute;
      top: 0;
      left: 0;
      width: ${({ width }) => (width ? `${width}px` : `100%`)};
      height: ${({ height }) => (height ? `${height}px` : `100%`)};
    }
    &.default {
      padding: 20px;
      background-color: #fff;
      border: 1px solid #333;
    }
    .dropzone__container {
      outline: none;
    }
    .dropzoneView {
      border: 1px solid #333;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }
    .dropzoneView__picture_box {
      text-align: center;
      .dropzoneView__picture_img {
      }
      .dropzoneView__picture_text {
        margin-top: 15px;
      }
    }
  `,
};

export default Dropzone;